
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import TextType from '../components/ui/TextType';
import ShinyText from '../components/ui/ShinyText';
import { Mail, Lock, Moon, Sun, Eye, EyeOff } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError('Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-white dark:bg-background px-4 sm:px-6 lg:px-8">
      {/* Theme Toggle Button */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Toggle theme clicked, current mode:', isDarkMode);
            toggleTheme();
          }}
          className="p-2 rounded-lg dark:text-text-secondary text-light-text-secondary dark:hover:text-text-primary hover:text-light-text-primary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors relative overflow-hidden w-8 h-8 sm:w-9 sm:h-9 dark:bg-surface bg-light-surface border dark:border-border-color border-light-border-color shadow-lg"
          aria-label="Toggle theme"
          type="button"
        >
        <div className="relative w-full h-full">
          <Sun 
            className={`w-5 h-5 absolute top-1/2 -translate-y-1/2 transition-all duration-300 ${
              isDarkMode 
                ? 'left-1/2 -translate-x-1/2 opacity-100' 
                : 'left-full translate-x-0 opacity-0'
            }`}
          />
          <Moon 
            className={`w-5 h-5 absolute top-1/2 -translate-y-1/2 transition-all duration-300 ${
              !isDarkMode 
                ? 'left-1/2 -translate-x-1/2 opacity-100' 
                : '-left-full -translate-x-0 opacity-0'
            }`}
          />
        </div>
      </button>
      </div>

      <div className="w-full max-w-4xl px-4 sm:px-0">
        <div className="h-20 sm:h-32 flex items-center justify-center -mt-4 sm:-mt-8">
          <TextType 
            text={["Welcome to Mentoros — empowering your business with intelligent customer engagement.","Your 25/7 AI sales assistant — built to guide shoppers and drive conversions."]}
            typingSpeed={75}
            pauseDuration={1500}
            showCursor={true}
            cursorCharacter="|"
            className="text-center text-lg sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900 dark:text-[#e0e7ff] mb-[8rem] sm:mb-[12rem] md:mb-[18rem] leading-tight"
          />
        </div>
        <div className="w-full max-w-md mx-auto -mt-[3rem] sm:-mt-[4rem] md:-mt-[5rem]">
          <div>
            <h1 className="text-center text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              <ShinyText text="MentorOS" speed={5} />
            </h1>
            <h2 className="mt-2 sm:mt-4 text-center text-base sm:text-lg text-text-secondary">
              Merchant Dashboard Login
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px flex flex-col gap-4">
                <Input 
                    id="email-address"
                    label="Email address"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    icon={<Mail className="h-5 w-5 text-gray-500 dark:text-gray-400" />}
                />
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 
                                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                placeholder-gray-500 dark:placeholder-gray-400
                                shadow-sm focus:border-primary focus:ring-primary 
                                sm:text-sm py-2 pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 dark:text-gray-400"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
            </div>
          
            {error && <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>}

            <div>
                <Button type="submit" className="w-full" isLoading={isLoading}>
                    Sign in
                </Button>
            </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
